import type { PublicVenue } from '@/types/events'

/**
 * Build a Google Maps search URL for a venue.
 *
 * VERBATIM MIRROR of apps/event-site/src/utils/googleMapsUrl.ts, which remains
 * the source of truth (#185). Everything except imports and comments must stay
 * byte-for-byte identical to that file - including its 4-space indentation,
 * which is why this file does not follow the 2-space style of its neighbours.
 * Do not reformat or add SPA-specific behaviour here.
 * googleMapsUrl.parity.spec.ts fails if the two files diverge.
 *
 * Priority:
 * 1. Coordinate query when both latitude and longitude are present and non-empty.
 * 2. Address-string search built from the available address parts.
 * 3. Returns null when no usable data is available.
 *
 * Uses the documented Maps URL API form (/maps/search/?api=1&query=...)
 * for forward compatibility. All query values are passed through encodeURIComponent.
 */
export function buildGoogleMapsUrl(venue: PublicVenue): string | null {
    if (venue.latitude && venue.longitude) {
        const query = encodeURIComponent(`${venue.latitude},${venue.longitude}`)
        return `https://www.google.com/maps/search/?api=1&query=${query}`
    }

    const parts: string[] = [
        venue.name,
        venue.street,
        venue.postal_code,
        venue.city,
        venue.country,
    ].filter((p): p is string => typeof p === 'string' && p.trim().length > 0)

    if (parts.length === 0) {
        return null
    }

    const query = encodeURIComponent(parts.join(', '))
    return `https://www.google.com/maps/search/?api=1&query=${query}`
}
